import { useState } from "react";
import { Check } from "lucide-react";
import type { CoachingVisibility } from "@/lib/types";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { Button } from "@/components/ui/Button";
import { visibilityOptions } from "../visibilityVocabulary";
import { CoachModal } from "./CoachModal";

/**
 * A6 — bulk-action toolbar for the Feedback Library list pages
 * (Notes / Observations / Clips). Appears once at least one row is
 * selected: shows the count, a "Set visibility" select that applies
 * to every selected row, a Delete action, and a Clear. The pages own
 * the selection state + the actual mutations; this is pure
 * presentation.
 *
 * The visibility options mirror the composer's closed set. When
 * "Player + family" is chosen and one or more selected rows have NO
 * tagged players, the parent passes `untaggedWarning` so the coach is
 * told those rows won't reach a player (player-visibility with zero
 * tags is invisible to viewers — same gotcha the composer guards).
 */

// One vocabulary for every composer — see visibilityVocabulary.ts.
const VISIBILITY_OPTIONS = visibilityOptions().map((option) =>
  option.value === "private" ? { ...option, label: "Coaches" } : option,
);

export type BulkSelectionBarProps = {
  count: number;
  /** Disable controls while a bulk op is in flight. */
  busy?: boolean;
  /** Non-null when "Player + family" is the pending choice AND some
   * selected rows have zero tagged players — shown as an inline
   * caution under the bar. */
  untaggedWarning?: string | null;
  onSetVisibility: (visibility: CoachingVisibility) => void;
  onDelete: () => void;
  onClear: () => void;
  /** Distinguishes the Notes vs Clips bar in tests. */
  testIdPrefix: string;
};

export function BulkSelectionBar({
  count,
  busy = false,
  untaggedWarning,
  onSetVisibility,
  onDelete,
  onClear,
  testIdPrefix,
}: BulkSelectionBarProps) {
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [visibility, setVisibility] = useState<CoachingVisibility>("private");
  if (count === 0) return null;
  return (
    <>
      <div
        className="flex flex-col gap-2 rounded bg-elevated p-3"
        data-testid={`${testIdPrefix}-bulk-bar`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="font-display text-sm text-ink"
            data-testid={`${testIdPrefix}-bulk-count`}
          >
            {count} selected
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setVisibilityOpen(true)}
              disabled={busy}
              data-testid={`${testIdPrefix}-bulk-visibility`}
            >
              Change visibility
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={busy}
              data-testid={`${testIdPrefix}-bulk-delete`}
              className="text-destructive"
            >
              Delete selected
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              disabled={busy}
              data-testid={`${testIdPrefix}-bulk-clear`}
            >
              Clear selection
            </Button>
          </div>
        </div>
        {untaggedWarning ? (
          <p
            className="text-xs text-flag"
            data-testid={`${testIdPrefix}-bulk-untagged-warning`}
          >
            {untaggedWarning}
          </p>
        ) : null}
      </div>

      {visibilityOpen ? (
        <CoachModal
          title="Change visibility"
          onClose={() => setVisibilityOpen(false)}
          data-testid={`${testIdPrefix}-bulk-visibility-modal`}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setVisibilityOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={busy}
                onClick={() => {
                  onSetVisibility(visibility);
                  setVisibilityOpen(false);
                }}
              >
                Apply visibility
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display text-ink">Visibility</span>
              <TkticSelect
                value={visibility}
                onChange={(next) =>
                  setVisibility(next as CoachingVisibility)
                }
                ariaLabel="Set visibility for selected"
                data-testid={`${testIdPrefix}-bulk-visibility-select`}
                options={VISIBILITY_OPTIONS}
              />
            </label>
            <p className="text-sm text-ink-muted">
              Applies to {count} selected item{count === 1 ? "" : "s"}.
            </p>
            {visibility === "player" && untaggedWarning ? (
              <p className="text-xs text-flag">{untaggedWarning}</p>
            ) : null}
          </div>
        </CoachModal>
      ) : null}
    </>
  );
}

/**
 * The per-row selection toggle — a styled checkbox (no native
 * `<input type=checkbox>` per the no-native-chrome rule). `aria-pressed`
 * carries the state for assistive tech.
 */
export function SelectToggle({
  selected,
  onToggle,
  label,
  testid,
}: {
  selected: boolean;
  onToggle: () => void;
  label: string;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      aria-label={label}
      title={label}
      data-testid={testid}
      className={[
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
        selected
          ? "border-accent bg-accent text-accent-ink"
          : "border-rule-strong bg-transparent text-transparent hover:bg-field",
      ].join(" ")}
    >
      <Check size={13} strokeWidth={3} aria-hidden="true" />
    </button>
  );
}
